import React from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Download, Mail, Play, RefreshCw, AlertTriangle, Briefcase,
} from 'lucide-react';

const formatMoney = (amount) => {
  if (!amount && amount !== 0) return '$0';
  return new Intl.NumberFormat('es-AR', {
    style: 'currency', currency: 'ARS',
    minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(amount);
};

export const InvestigacionDialogs = ({
  investigation, resultados,
  // Email dialog
  emailDialog, setEmailDialog, handleSendEmail, sendingEmail,
  // Case dialog
  caseDialog, setCaseDialog, handleCreateCase, creatingCase,
  // Rerun dialog
  rerunDialog, setRerunDialog, handleRerunAnalysis, rerunning,
  // PDF dialog
  pdfDialog, setPdfDialog, handleExportPDF, generatingPdf,
  handleTogglePdfClient, handleSelectAllPdfClients, handleDeselectAllPdfClients,
}) => (
  <>
    {/* Modal de envío de email */}
    {emailDialog.open && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setEmailDialog({ ...emailDialog, open: false })}>
        <Card className="w-full max-w-lg m-4 bg-white p-6" onClick={e => e.stopPropagation()}>
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Mail size={20} className="text-purple-600" />
            Enviar Reporte por Email
          </h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Destinatarios *</label>
              <Input value={emailDialog.recipients}
                onChange={(e) => setEmailDialog({ ...emailDialog, recipients: e.target.value })}
                placeholder="email1@empresa.com, email2@empresa.com"
                style={{ backgroundColor: '#fff' }} />
              <p className="text-xs text-slate-400 mt-1">Separar múltiples emails con coma</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Mensaje adicional (opcional)</label>
              <Textarea value={emailDialog.message}
                onChange={(e) => setEmailDialog({ ...emailDialog, message: e.target.value })}
                placeholder="Agregue observaciones o notas para los destinatarios..."
                rows={3} style={{ backgroundColor: '#fff' }} />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="includePdf" checked={emailDialog.includePdf}
                onChange={(e) => setEmailDialog({ ...emailDialog, includePdf: e.target.checked })} className="rounded" />
              <label htmlFor="includePdf" className="text-sm text-slate-600">Adjuntar PDF del reporte</label>
            </div>
          </div>
          <div className="flex gap-2 mt-6 justify-end">
            <Button variant="outline" onClick={() => setEmailDialog({ ...emailDialog, open: false })}>Cancelar</Button>
            <Button onClick={handleSendEmail} disabled={sendingEmail}
              style={{ backgroundColor: '#7c3aed', color: 'white' }} className="text-white">
              {sendingEmail ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <Mail size={14} className="mr-1" />}
              Enviar
            </Button>
          </div>
        </Card>
      </div>
    )}

    {/* Modal de creación de caso */}
    {caseDialog.open && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setCaseDialog({ ...caseDialog, open: false })}>
        <Card className="w-full max-w-lg m-4 bg-white p-6" onClick={e => e.stopPropagation()}>
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Briefcase size={20} className="text-purple-600" />
            Crear Caso desde Investigación
          </h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Título del Caso (opcional)</label>
              <Input value={caseDialog.titulo}
                onChange={(e) => setCaseDialog({ ...caseDialog, titulo: e.target.value })}
                placeholder={`Caso desde INV-${String(investigation.numero_investigacion).padStart(4, '0')}`}
                style={{ backgroundColor: '#fff' }} />
              <p className="text-xs text-slate-400 mt-1">Dejar vacío para usar título automático</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Prioridad</label>
              <Select value={caseDialog.prioridad} onValueChange={(v) => setCaseDialog({ ...caseDialog, prioridad: v })}>
                <SelectTrigger style={{ backgroundColor: '#fff' }}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="BAJA">Baja</SelectItem>
                  <SelectItem value="MEDIA">Media</SelectItem>
                  <SelectItem value="ALTA">Alta</SelectItem>
                  <SelectItem value="CRITICA">Crítica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Observaciones adicionales (opcional)</label>
              <Textarea value={caseDialog.descripcion}
                onChange={(e) => setCaseDialog({ ...caseDialog, descripcion: e.target.value })}
                placeholder="Agregue notas o contexto adicional para el caso..."
                rows={3} style={{ backgroundColor: '#fff' }} />
            </div>
            <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
              <p className="font-medium mb-1">El caso incluirá automáticamente:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Clientes investigados como cuentas relacionadas</li>
                <li>Resumen del análisis y patrones detectados</li>
                <li>Comentarios para informe (si existen)</li>
                <li>Referencia a la investigación original</li>
              </ul>
            </div>
          </div>
          <div className="flex gap-2 mt-6 justify-end">
            <Button variant="outline" onClick={() => setCaseDialog({ ...caseDialog, open: false })}>Cancelar</Button>
            <Button onClick={handleCreateCase} disabled={creatingCase}
              style={{ backgroundColor: '#7c3aed', color: 'white' }} className="text-white"
              data-testid="confirm-create-case-btn">
              {creatingCase ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <Briefcase size={14} className="mr-1" />}
              Crear Caso
            </Button>
          </div>
        </Card>
      </div>
    )}

    {/* Modal de re-ejecutar análisis */}
    {rerunDialog.open && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setRerunDialog({ ...rerunDialog, open: false })}>
        <Card className="w-full max-w-lg m-4 bg-white p-6" onClick={e => e.stopPropagation()}>
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Play size={20} className="text-purple-600" />
            Re-ejecutar Análisis
          </h3>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Puede modificar las fechas y volver a ejecutar el análisis para obtener datos actualizados.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Fecha Desde</label>
                <Input type="date" value={rerunDialog.fechaDesde}
                  onChange={(e) => setRerunDialog({ ...rerunDialog, fechaDesde: e.target.value })}
                  style={{ backgroundColor: '#fff' }} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Fecha Hasta</label>
                <Input type="date" value={rerunDialog.fechaHasta}
                  onChange={(e) => setRerunDialog({ ...rerunDialog, fechaHasta: e.target.value })}
                  style={{ backgroundColor: '#fff' }} />
              </div>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-700 flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
              <p>Los resultados anteriores serán reemplazados con los nuevos datos.</p>
            </div>
          </div>
          <div className="flex gap-2 mt-6 justify-end">
            <Button variant="outline" onClick={() => setRerunDialog({ ...rerunDialog, open: false })}>Cancelar</Button>
            <Button onClick={handleRerunAnalysis} disabled={rerunning}
              style={{ backgroundColor: '#7c3aed', color: 'white' }} className="text-white"
              data-testid="confirm-rerun-btn">
              {rerunning ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <Play size={14} className="mr-1" />}
              Ejecutar Análisis
            </Button>
          </div>
        </Card>
      </div>
    )}

    {/* Modal de selección de PDF */}
    {pdfDialog.open && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setPdfDialog({ ...pdfDialog, open: false })}>
        <Card className="w-full max-w-2xl m-4 bg-white p-6" onClick={e => e.stopPropagation()}>
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Download size={20} className="text-purple-600" />
            Generar Reporte PDF
          </h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">Tipo de reporte</label>
              <div className="flex gap-2">
                <Button variant={pdfDialog.mode === 'completo' ? "default" : "outline"} size="sm"
                  onClick={() => setPdfDialog(prev => ({ ...prev, mode: 'completo' }))}
                  className={pdfDialog.mode === 'completo' ? "bg-purple-600 text-white hover:bg-purple-700" : ""}>
                  Reporte Completo
                </Button>
                <Button variant={pdfDialog.mode === 'seleccion' ? "default" : "outline"} size="sm"
                  onClick={() => setPdfDialog(prev => ({ ...prev, mode: 'seleccion' }))}
                  className={pdfDialog.mode === 'seleccion' ? "bg-purple-600 text-white hover:bg-purple-700" : ""}>
                  Seleccionar Clientes
                </Button>
              </div>
            </div>

            {pdfDialog.mode === 'seleccion' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-slate-700">
                    Clientes a incluir ({pdfDialog.selectedClients.length} seleccionados)
                  </label>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleSelectAllPdfClients} className="text-xs">Todos</Button>
                    <Button variant="outline" size="sm" onClick={handleDeselectAllPdfClients} className="text-xs">Ninguno</Button>
                  </div>
                </div>
                <div className="max-h-60 overflow-y-auto border border-slate-200 rounded-lg p-3 space-y-2">
                  {resultados?.analisis_individual?.map((client) => (
                    <div key={client.cliente_id} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded">
                      <input type="checkbox" id={`client-${client.cliente_id}`}
                        checked={pdfDialog.selectedClients.includes(client.cliente_id)}
                        onChange={() => handleTogglePdfClient(client.cliente_id)} className="rounded" />
                      <label htmlFor={`client-${client.cliente_id}`} className="flex-1 cursor-pointer">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-slate-700">
                              {client.perfil?.denominacion || client.perfil?.razon_social || 'Sin nombre'}
                            </p>
                            <p className="text-xs text-slate-400 font-mono">{client.cliente_id}</p>
                          </div>
                          <div className="text-right text-xs text-slate-500">
                            <p>{client.metricas?.total_transacciones || 0} txs</p>
                            <p>{formatMoney(client.metricas?.volumen_total || 0)}</p>
                          </div>
                        </div>
                      </label>
                    </div>
                  )) || []}
                </div>
              </div>
            )}

            <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
              <p className="font-medium mb-1">
                {pdfDialog.mode === 'completo' ? 'Reporte completo incluirá:' : 'Reporte personalizado incluirá:'}
              </p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Resumen ejecutivo de la investigación</li>
                <li>{pdfDialog.mode === 'completo' ? 'Análisis de todos los clientes' : `Análisis de ${pdfDialog.selectedClients.length} cliente(s) seleccionado(s)`}</li>
                <li>Patrones detectados y métricas</li>
                <li>Gráficos y visualizaciones</li>
                {investigation.tipo_analisis === 'individual_relacionado' && <li>Análisis de relaciones y cruces</li>}
              </ul>
            </div>
          </div>
          <div className="flex gap-2 mt-6 justify-end">
            <Button variant="outline" onClick={() => setPdfDialog({ open: false, mode: 'completo', selectedClients: [] })}>Cancelar</Button>
            <Button onClick={handleExportPDF}
              disabled={generatingPdf || (pdfDialog.mode === 'seleccion' && pdfDialog.selectedClients.length === 0)}
              style={{ backgroundColor: '#7c3aed', color: 'white' }} className="text-white"
              data-testid="confirm-export-pdf-btn">
              {generatingPdf ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <Download size={14} className="mr-1" />}
              Generar PDF
            </Button>
          </div>
        </Card>
      </div>
    )}
  </>
);

export default InvestigacionDialogs;
